import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  createRef,
  useLayoutEffect,
} from 'react';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import ProductCard from '../ProductCards/ProductCard';
import ProductCardPlaceholder from '../ProductCards/ProductCardPlaceholder';
import { ProductListingType } from '../../Reducers/ProductListing.reducer';
import {
  fetchProducts,
  setIsLoadingMore,
  setIsLoadingReverse,
  setUrlCount,
} from '../../Actions/ProductListing.action';
import { throttle } from 'lodash-es';
import { translate } from '../../Services/translation';
import { useTypedDispatch } from '../../Hooks/useTypedDispatch';
import useReady from '../../Hooks/useReady';
import Grid1plus2 from './Layouts/Grid1plus2';
import Grid from './Layouts/Grid';
import Grid3by3 from './Layouts/Grid3by3';
import RenderIfVisible from 'react-render-if-visible';
import clsx from 'clsx';
import DetailedList from './Layouts/DetailedList';
import ProductCardWithDetailsContainer from '../ProductCards/ProductCardWithDetailsContainer';
import ProductCardWithDetailsPlaceholderContainer from '../ProductCards/DetailedProductCardComponents/ProductCardWithDetailsPlaceholderContainer';
import Product from '../../Types/Product';
import BannerBlock, {
  BannerBlockViewModel,
} from '../../Blocks/BannerBlock/BannerBlock';
import useIsMobile from '../../Hooks/useIsMobile';
import useWindowSize from '../../Hooks/useWindowSize';
const MAX_BANNER_COUNT = 3;
const getScrollStorageKey = () => `scroll-${document.location.toString()}`;
const getBannerIndices = (
  bannerCount: number,
  productListingType: ProductListingType,
  isMobile: boolean
) => {
  let indices: number[] = [];

  if (bannerCount == 0) return indices;

  if (productListingType == ProductListingType.Grid1plus2) {
    indices = isMobile ? [10, 25, 40] : [6, 19, 34];
  } else if (productListingType == ProductListingType.Grid2by2) {
    indices = [8, 23, 32];
  } else if (productListingType == ProductListingType.Grid3by3) {
    indices = [12, 33, 46];
  }

  return indices.slice(0, Math.min(MAX_BANNER_COUNT, bannerCount));
};
const getShiftCount = (
  bannerIndices: number[],
  bannerCount: number,
  index: number
) => {
  for (let i = 1; i <= bannerIndices.length; i++) {
    if (
      bannerCount >= i &&
      index > bannerIndices[i - 1]! &&
      (bannerCount < i + 1 || index <= bannerIndices[i]!)
    ) {
      return i;
    }
  }

  return 0;
};
const isLargeCard = (
  productListingType: ProductListingType,
  index: number,
  shiftCount: number = 0
) => {
  const shiftedIndex = index + shiftCount;

  return (
    productListingType == ProductListingType.Grid1plus2 &&
    ((shiftedIndex - 2) % 10 === 0 || (shiftedIndex - 5) % 10 === 0)
  );
};
const isMobileLargeCard = (
  productListingType: ProductListingType,
  index: number
) => {
  const shiftedIndex = index;
  return (
    productListingType == ProductListingType.Grid1plus2 &&
    shiftedIndex % 5 === 0
  );
};

const bannerShouldSpanTwoCols = (
  productListingType: ProductListingType,
  isMobile: boolean
) => {
  if (productListingType == ProductListingType.Grid3by3 && isMobile) {
    return false;
  }

  if (productListingType == ProductListingType.Grid2by2 && !isMobile) {
    return false;
  }
  return true;
};

const elementIsVisibleInViewport = (el, partiallyVisible = false) => {
  const { top, left, bottom, right } = el.getBoundingClientRect();
  const { innerHeight, innerWidth } = window;
  return partiallyVisible
    ? ((top > 0 && top < innerHeight) ||
        (bottom > 0 && bottom < innerHeight)) &&
        ((left > 0 && left < innerWidth) ||
          (right > 0 && right < innerWidth))
    : top >= 0 &&
        left >= 0 &&
        bottom <= innerHeight &&
        right <= innerWidth;
};

const isBannerBlock = (item: Product | BannerBlockViewModel | undefined) =>
  !!item && !!(item as BannerBlockViewModel).systemId;

const ProductListing = () => {
  const dispatch = useTypedDispatch();
  const ready = useReady();
  const {
    products,
    totalProductsCount,
    fromCount,
    isLoadingMore,
    isLoadingReverse,
    productListingType,
    showAllFallbackUrl,
    pageSize,
    showRoomImage,
    renderProducts,
    banners,
  } = useTypedSelector((state) => state.productListing);

  const [infiniteScrollEnabled, setInfiniteScrollEnabled] = useState(
    !!fromCount
  );
  const [mostLoadedGroupPosY, setMostLoadedGroupPosY] = useState<
    number | undefined
  >();
  const [leastLoadedGroupPosY, setLeastLoadedGroupPosY] = useState<
    number | undefined
  >();
  const [loadThreshold, setLoadThreshold] = useState(5000);
  const [isResizing, setIsResizing] = useState(false);
  const [windowWidth] = useWindowSize();
  const isReady = useReady();

  const hasLoadedAll = useMemo(
    () => totalProductsCount <= products.filter((p) => !!p).length,
    [totalProductsCount, products]
  );
  const [initialProducts] = useState(products); // keep track of what products we initially had
  const isMobile = useIsMobile();
  const bannerIndices = useMemo(
    () =>
      getBannerIndices(banners?.length ?? 0, productListingType, isMobile),
    [banners?.length, productListingType, isMobile]
  );

  const bannerCount = useMemo(
    () =>
      bannerIndices
        .slice(0, (banners?.length ?? 0) + 1)
        .filter((idx) => idx < products.length).length,
    [bannerIndices, banners?.length, products.length]
  );

  const productsWithPlaceholders = useMemo<
    (Product | BannerBlockViewModel | undefined)[]
  >(() => {
    if (!renderProducts) {
      // Placeholder cards when rendered on the server
      return Array.from({ length: pageSize }) as Product[];
    }

    const _products: (Product | BannerBlockViewModel | undefined)[] =
      infiniteScrollEnabled
        ? products.concat(
            Array.from({
              length:
                totalProductsCount - products.length - (fromCount ?? 0),
            })
          )
        : [...products];

    if (initialProducts.length > 0) return _products;

    // Insert banner blocks into product listing
    if (banners?.length > 0) {
      const bannerIndices = getBannerIndices(
        banners?.length ?? 0,
        productListingType,
        isMobile
      );

      for (let bannerIndex = 0; bannerIndex < bannerCount; bannerIndex++) {
        if (banners) {
          const banner = banners[bannerIndex];
          const indexInProductList = bannerIndices[bannerIndex];
          if (typeof indexInProductList === 'undefined') {
            continue;
          }

          _products.splice(indexInProductList, 0, banner);
        }
      }
    }

    return _products;
  }, [
    renderProducts,
    infiniteScrollEnabled,
    products,
    totalProductsCount,
    fromCount,
    initialProducts.length,
    banners,
    productListingType,
    pageSize,
    bannerCount,
    isMobile,
  ]);

  const productRefs = useMemo(
    () =>
      renderProducts
        ? productsWithPlaceholders.map((p) =>
            !!p ? createRef<HTMLDivElement>() : undefined
          )
        : [],
    [productsWithPlaceholders, renderProducts]
  );

  const [ProductComponent, ProductPlaceholderComponent] = useMemo(() => {
    switch (productListingType) {
      case ProductListingType.Grid:
      case ProductListingType.Grid1plus2:
      case ProductListingType.Grid2by2:
      case ProductListingType.Grid3by3:
      case ProductListingType.SingleCard:
      default:
        return [ProductCard, ProductCardPlaceholder];
      case ProductListingType.DetailedList:
        return [
          ProductCardWithDetailsContainer,
          ProductCardWithDetailsPlaceholderContainer,
        ];
    }
  }, [productListingType]);

  /**
   * The least (top) and most (bottom) loaded product group indexes.
   * Used to determine when to load more/reverse
   */
  const mostLoadedIndex = useMemo(
    () =>
      hasLoadedAll || !renderProducts
        ? -1
        : productsWithPlaceholders.findLastIndex((p) => !!p),
    [productsWithPlaceholders, hasLoadedAll, renderProducts]
  );

  const leastLoadedIndex = useMemo(
    () =>
      hasLoadedAll || !renderProducts
        ? -1
        : productsWithPlaceholders.findIndex((p) => !!p),
    [productsWithPlaceholders, hasLoadedAll, renderProducts]
  );

  /**
   * Fetches more products based on the most loaded index.
   * @returns void
   */
  const loadMore = () => {
    if (!infiniteScrollEnabled || isLoadingMore || hasLoadedAll) return;

    const startIndex = mostLoadedIndex + 1;

    dispatch(setIsLoadingMore(true));
    dispatch(fetchProducts(startIndex)).finally(() => {
      dispatch(setIsLoadingMore(false));
    });
  };

  /**
   * Fetches products in reverse, aka. on scroll up, based on the least loaded index.
   * @returns void
   */
  const loadReverse = () => {
    if (leastLoadedIndex == 0 || isLoadingReverse || hasLoadedAll) {
      return;
    }

    let startIndex = leastLoadedIndex - pageSize;
    let take: number | undefined = undefined;
    if (startIndex < 0) {
      take = pageSize + startIndex;
      startIndex = 0;
    }

    dispatch(setIsLoadingReverse(true));
    dispatch(fetchProducts(startIndex, take)).finally(() => {
      dispatch(setIsLoadingReverse(false));
    });
  };

  /**
   * When clicking the button to show all products, which enables infinite scroll.
   */
  const onShowAllProducts = (
    e: React.MouseEvent | React.KeyboardEvent
  ) => {
    if (
      e.type === 'keydown' &&
      (e as React.KeyboardEvent).key !== 'Enter'
    ) {
      return;
    }
    e.preventDefault();
    setInfiniteScrollEnabled(true);
  };

  /**
   * Updates the stored positions for the least and most loaded product groups.
   * These values are used to determine when to load more/reverse products.
   */
  const refreshLoadedGroupPositions = () => {
    const leastLoadedGroupRef = productRefs.find((ref) => !!ref?.current);
    const mostLoadedGroupRef = productRefs.findLast(
      (ref) => !!ref?.current
    );
    const mostLoadedProductGroupPosY =
      mostLoadedGroupRef?.current?.getBoundingClientRect().top;
    let leastLoadedProductGroupPosY =
      leastLoadedGroupRef?.current?.getBoundingClientRect().top;

    if (leastLoadedIndex == mostLoadedIndex) {
      leastLoadedProductGroupPosY = mostLoadedProductGroupPosY;
    }

    setMostLoadedGroupPosY(mostLoadedProductGroupPosY);
    setLeastLoadedGroupPosY(leastLoadedProductGroupPosY);
  };

  /**
   * Infinite scroll.
   * Detect when to load more products
   */
  useEffect(() => {
    if (
      infiniteScrollEnabled &&
      !isLoadingMore &&
      mostLoadedGroupPosY &&
      mostLoadedGroupPosY + window.innerHeight <= loadThreshold
    ) {
      loadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostLoadedGroupPosY, infiniteScrollEnabled]);

  /**
   * Infinite reverse scroll.
   * Detect when to load products reverse.
   */
  useEffect(() => {
    if (
      !!fromCount &&
      !isLoadingReverse &&
      leastLoadedGroupPosY &&
      leastLoadedGroupPosY >= 0 - loadThreshold
    ) {
      loadReverse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leastLoadedGroupPosY, leastLoadedIndex]);

  /**
   * Following two useEffects are used to update the stored positions of the most & least loaded product groups,
   * which is what is used to determine when to load more/reverse
   */
  useEffect(() => {
    if (!renderProducts || !infiniteScrollEnabled) {
      return;
    }

    const onScroll = throttle(
      () => {
        refreshLoadedGroupPositions();
      },
      100,
      { leading: true, trailing: false }
    );

    window.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [infiniteScrollEnabled, renderProducts, productRefs]);

  useEffect(() => {
    if (!renderProducts) return;

    const refreshIntervalId = setInterval(() => {
      refreshLoadedGroupPositions();
      if (totalProductsCount <= products.length) {
        clearInterval(refreshIntervalId);
      }
    }, 250);

    return () => {
      clearInterval(refreshIntervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingMore, isLoadingReverse, renderProducts]);

  /**
   * Updates the "count" parameter in the url, to store how many products we've loaded.
   */
  useEffect(() => {
    if (!infiniteScrollEnabled) return;

    dispatch(setUrlCount(products.length));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products.length]);

  const wrapperClasses = useMemo(() => {
    switch (productListingType) {
      case ProductListingType.Grid:
        return 'grid grid-cols-1 gap-4';
      default:
        return undefined;
    }
  }, [productListingType]);

  const CurrentProductsLayout = useMemo(() => {
    switch (productListingType) {
      case ProductListingType.Grid1plus2:
        return Grid1plus2;

      case ProductListingType.Grid3by3:
        return Grid3by3;
      case ProductListingType.DetailedList:
        return DetailedList;
      case ProductListingType.Grid2by2:
      default:
        return Grid;
    }
  }, [productListingType]);

  /**
   * Logic to keep scroll position on page load
   */
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const onScroll = throttle(
      () => setScrollY(window.scrollY - window.outerHeight),
      500
    );

    window.addEventListener('scroll', onScroll);
    const scrollStorageKey = getScrollStorageKey();
    const initialScrollY = parseInt(
      sessionStorage.getItem(scrollStorageKey) ?? '0'
    );

    if (initialScrollY > 0) {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }
      setTimeout(() => {
        window.scrollTo({ top: initialScrollY, behavior: 'instant' });
        sessionStorage.removeItem(scrollStorageKey);
      }, 100);
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const onBeforeUnload = useCallback(() => {
    const topMostIndexInView = productRefs.findIndex(
      (ref) =>
        ref?.current && elementIsVisibleInViewport(ref.current, false)
    );

    if (topMostIndexInView == 0 || topMostIndexInView >= pageSize) {
      dispatch(setUrlCount(topMostIndexInView));
    }

    if (infiniteScrollEnabled) {
      sessionStorage.setItem(getScrollStorageKey(), scrollY.toString());
    }
  }, [scrollY, infiniteScrollEnabled, productRefs]);

  useEffect(() => {
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [onBeforeUnload]);

  useLayoutEffect(() => {
    if (!isReady) return;

    setIsResizing(true);
    const timerId = setTimeout(() => setIsResizing(false), 100);
    return () => {
      clearTimeout(timerId);
    };
  }, [windowWidth]);

  return (
    <>
      <div id="productListingAnchor" className={wrapperClasses}>
        <CurrentProductsLayout>
          {productsWithPlaceholders.map((p, idx) => {
            return (
              <div
                data-p-index={idx}
                ref={renderProducts ? productRefs[idx] : undefined}
                key={idx + '-product'}
                className={clsx(
                  !isMobile &&
                    isLargeCard(
                      productListingType,
                      idx,
                      initialProducts.length > 0
                        ? 0
                        : getShiftCount(bannerIndices, bannerCount, idx)
                    ) &&
                    'md:col-span-2 md:row-span-2',
                  isMobile &&
                    !isBannerBlock(p) &&
                    isMobileLargeCard(productListingType, idx) &&
                    'sm-down:col-span-2 sm-down:row-span-2',
                  isBannerBlock(p) && {
                    'col-span-2': bannerShouldSpanTwoCols(
                      productListingType,
                      isMobile
                    ),
                    'sm-down:row-span-2':
                      productListingType == ProductListingType.Grid1plus2,
                    'aspect-square':
                      (productListingType ==
                        ProductListingType.Grid1plus2 ||
                        productListingType ==
                          ProductListingType.Grid2by2 ||
                        productListingType ==
                          ProductListingType.Grid3by3) &&
                      isMobile,
                  }
                )}
              >
                {!isResizing && (
                  <RenderIfVisible
                    initialVisible={!ready || !!initialProducts[idx]}
                    visibleOffset={5000}
                    rootElementClass={clsx(
                      isLargeCard(
                        productListingType,
                        idx,
                        initialProducts.length > 0
                          ? 0
                          : getShiftCount(bannerIndices, bannerCount, idx)
                      ) &&
                        '[&_.image-wrapper>div]:md:!aspect-auto [&_.image-wrapper>div]:md:h-full [&_.image-wrapper]:md:h-full [&_img]:md:!aspect-auto [&_img]:md:h-full',
                      'h-full'
                    )}
                  >
                    {!p && <ProductPlaceholderComponent />}
                    {!!p && !!(p as Product).id && (
                      <ProductComponent
                        product={p as Product}
                        showRoomshot={showRoomImage}
                        showPatternImage={!showRoomImage}
                      />
                    )}
                    {isBannerBlock(p) && (
                      <BannerBlock
                        {...(p as BannerBlockViewModel)}
                        inProductListing={true}
                      />
                    )}
                  </RenderIfVisible>
                )}
              </div>
            );
          })}
        </CurrentProductsLayout>
      </div>
      {!infiniteScrollEnabled && !hasLoadedAll && (
        <div className="mt-4 flex justify-center">
          <a
            className="btn btn--primary btn--medium"
            onClick={onShowAllProducts}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onShowAllProducts(e);
              }
            }}
            href={ready ? undefined : showAllFallbackUrl}
            tabIndex={0}
          >
            {translate('general.show-all')}
          </a>
        </div>
      )}
    </>
  );
};

export default ProductListing;
